import { useEffect, useState } from "react";
import { api, apiError, fmtDate } from "../lib/api";
import { toast } from "sonner";
import { LineChart, Line, ResponsiveContainer, XAxis, YAxis, Tooltip, BarChart, Bar, Legend, CartesianGrid, ReferenceLine } from "recharts";
import { TrendingUp, AlertTriangle, Clock, Users, Download } from "lucide-react";

const SITE_COLORS = ["#1E3355", "#E02B2B", "#059669", "#D97706", "#7C3AED", "#0891B2"];

export default function TrendsPage() {
    const [days, setDays] = useState(30);
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        setLoading(true);
        api.get("/dashboard/trends", { params: { days } })
            .then(({ data }) => setData(data))
            .catch((e) => toast.error(apiError(e)))
            .finally(() => setLoading(false));
    }, [days]);

    const completionData = data ? data.dates.map((d, i) => {
        const row = { date: d.slice(5) };
        Object.entries(data.site_series).forEach(([name, s]) => { row[name] = s.completion_pct[i]; });
        return row;
    }) : [];

    const escData = data ? data.dates.map((d, i) => {
        const row = { date: d.slice(5) };
        Object.entries(data.site_series).forEach(([name, s]) => { row[name] = s.escalation_count[i]; });
        return row;
    }) : [];

    return (
        <div className="mx-auto max-w-7xl px-4 sm:px-6 py-6 sm:py-10">
            <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-3">
                <div>
                    <div className="text-xs font-medium uppercase tracking-wider text-gray-500">Analytics</div>
                    <h1 className="font-display text-2xl sm:text-3xl font-bold text-[#1E3355] mt-1 flex items-center gap-2"><TrendingUp className="w-6 h-6" /> Trends</h1>
                    <p className="text-sm text-gray-500 mt-1">Last {days} days.</p>
                </div>
                <select value={days} onChange={(e) => setDays(Number(e.target.value))} className="text-sm rounded-md border border-gray-300 bg-white px-3 py-2" data-testid="trends-days-select">
                    <option value={7}>Last 7 days</option>
                    <option value={30}>Last 30 days</option>
                    <option value={60}>Last 60 days</option>
                    <option value={90}>Last 90 days</option>
                </select>
            </div>

            {loading || !data ? (
                <div className="mt-6 h-96 rounded-xl bg-white border border-gray-100 animate-pulse" />
            ) : (
                <div className="mt-6 space-y-6">
                    {/* Completion rate */}
                    <Card title="Checklist completion rate (%)" testId="trends-completion">
                        <div className="h-72">
                            <ResponsiveContainer>
                                <LineChart data={completionData} margin={{ top: 10, right: 20, bottom: 0, left: -10 }}>
                                    <CartesianGrid stroke="#E5E7EB" strokeDasharray="3 3" />
                                    <XAxis dataKey="date" tick={{ fontSize: 10 }} />
                                    <YAxis domain={[0, 100]} tick={{ fontSize: 10 }} />
                                    <Tooltip />
                                    <Legend wrapperStyle={{ fontSize: 11 }} />
                                    {Object.keys(data.site_series).map((s, i) => (
                                        <Line key={s} type="monotone" dataKey={s} stroke={SITE_COLORS[i % SITE_COLORS.length]} strokeWidth={2} dot={false} connectNulls />
                                    ))}
                                </LineChart>
                            </ResponsiveContainer>
                        </div>
                    </Card>

                    {/* Escalations */}
                    <Card title="Escalations per day" testId="trends-escalations">
                        <div className="h-64">
                            <ResponsiveContainer>
                                <BarChart data={escData} margin={{ top: 10, right: 20, bottom: 0, left: -10 }}>
                                    <CartesianGrid stroke="#E5E7EB" strokeDasharray="3 3" />
                                    <XAxis dataKey="date" tick={{ fontSize: 10 }} />
                                    <YAxis tick={{ fontSize: 10 }} allowDecimals={false} />
                                    <Tooltip />
                                    <Legend wrapperStyle={{ fontSize: 11 }} />
                                    {Object.keys(data.site_series).map((s, i) => (
                                        <Bar key={s} dataKey={s} stackId="a" fill={SITE_COLORS[i % SITE_COLORS.length]} />
                                    ))}
                                </BarChart>
                            </ResponsiveContainer>
                        </div>
                    </Card>

                    <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                        <Card title="Top-escalated tasks" testId="trends-top-escalated">
                            {data.top_escalated.length === 0 ? <div className="text-sm text-gray-500 py-6 text-center">No escalations in this window.</div> : (
                                <ul className="divide-y divide-gray-100">
                                    {data.top_escalated.map((e) => (
                                        <li key={e.task_number} className="py-2 flex items-start gap-2" data-testid={`top-esc-${e.task_number}`}>
                                            <div className="text-xs font-bold text-gray-400 w-8 tabular-nums">#{e.task_number}</div>
                                            <div className="flex-1 text-sm text-gray-800 min-w-0">{e.task_text}</div>
                                            <div className="text-sm font-bold text-[#E02B2B]">{e.count}×</div>
                                        </li>
                                    ))}
                                </ul>
                            )}
                        </Card>
                        <Card title="Avg SR response times (hours)" testId="trends-sr-response">
                            {data.sr_avg.length === 0 ? <div className="text-sm text-gray-500 py-6 text-center">No SRs in this window.</div> : (
                                <ul className="divide-y divide-gray-100">
                                    {data.sr_avg.map((s) => (
                                        <li key={s.site_name} className="py-2 flex items-center justify-between text-sm">
                                            <span className="font-semibold text-[#1E3355]">{s.site_name}</span>
                                            <span className="text-gray-600">ack {s.avg_response_hours ?? "—"}h · resolve {s.avg_resolution_hours ?? "—"}h</span>
                                        </li>
                                    ))}
                                </ul>
                            )}
                        </Card>
                    </div>

                    <Card title="Attendance reliability" testId="trends-attendance">
                        {data.attendance_reliability.length === 0 ? <div className="text-sm text-gray-500 py-6 text-center">No attendance data.</div> : (
                            <div className="overflow-x-auto">
                                <table className="w-full text-sm">
                                    <thead className="text-[11px] font-semibold uppercase tracking-wider text-gray-500 text-left">
                                        <tr><th className="pb-2">Site</th><th className="pb-2">Role</th><th className="pb-2">Present</th><th className="pb-2">Total</th><th className="pb-2">Present %</th></tr>
                                    </thead>
                                    <tbody className="divide-y divide-gray-100">
                                        {data.attendance_reliability.map((r, i) => (
                                            <tr key={`${r.site_name}-${r.role}-${i}`}>
                                                <td className="py-1.5">{r.site_name}</td>
                                                <td className="py-1.5">{r.role}</td>
                                                <td className="py-1.5">{r.present}</td>
                                                <td className="py-1.5">{r.total}</td>
                                                <td className="py-1.5 font-semibold">{r.present_pct != null ? `${r.present_pct}%` : "—"}</td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        )}
                    </Card>
                </div>
            )}
        </div>
    );
}

function Card({ title, testId, children }) {
    return (
        <div className="rounded-xl bg-white border border-gray-100 fx-shadow-card p-4 sm:p-5" data-testid={testId}>
            <div className="text-[11px] font-bold uppercase tracking-widest text-gray-500 mb-3">{title}</div>
            {children}
        </div>
    );
}
